import React, { useState, useCallback, useEffect } from 'react';
import './MemoryRipples.scss';

const MemoryRipples = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [ripples, setRipples] = useState([]);
  const [autoRipples, setAutoRipples] = useState([]);

  // Create ripple with enhanced physics
  const createRipple = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const rippleType = Math.random() > 0.7 ? 'memory' : 'temporal';
    
    const newRipple = {
      id: Date.now() + Math.random(),
      x,
      y,
      size: Math.random() * 120 + 80,
      color: rippleType === 'memory' 
        ? `hsl(${Math.random() * 60 + 180}, 70%, 60%)` // Blue to cyan
        : `hsl(${Math.random() * 60 + 280}, 70%, 60%)`, // Purple to magenta
      type: rippleType,
      intensity: Math.random() * 0.5 + 0.5,
      speed: Math.random() * 0.5 + 0.8,
      decay: Math.random() * 0.3 + 0.7,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.1,
      distortion: Math.random() * 0.5 + 0.5,
    };

    setRipples(prev => [...prev, newRipple]);

    // Remove ripple after animation
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 3000);
  }, []);

  // Auto-generate ripples for ambient effect
  useEffect(() => {
    if (isMobile) return;
    const interval = setInterval(() => {
      if (Math.random() > 0.7) { // 30% chance every interval
        const newAutoRipple = {
          id: Date.now() + Math.random(),
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 200 + 150,
          color: `hsl(${Math.random() * 60 + 200}, 60%, 50%)`,
          type: 'ambient',
          intensity: Math.random() * 0.3 + 0.2,
          speed: Math.random() * 0.3 + 0.5,
          decay: Math.random() * 0.4 + 0.6,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.05,
          distortion: Math.random() * 0.3 + 0.3,
        };

        setAutoRipples(prev => [...prev, newAutoRipple]);

        // Remove auto ripple after animation
        setTimeout(() => {
          setAutoRipples(prev => prev.filter(ripple => ripple.id !== newAutoRipple.id));
        }, 4000);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isMobile]);

  // Create ripple on mouse movement for more interactive feel
  const handleMouseMove = useCallback((event) => {
    if (isMobile) return;
    if (Math.random() > 0.95) { // 5% chance on mouse move
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      const newRipple = {
        id: Date.now() + Math.random(),
        x,
        y,
        size: Math.random() * 60 + 40,
        color: `hsl(${Math.random() * 40 + 190}, 70%, 60%)`,
        type: 'micro',
        intensity: Math.random() * 0.3 + 0.2,
        speed: Math.random() * 0.8 + 1.2,
        decay: Math.random() * 0.5 + 0.8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        distortion: Math.random() * 0.3 + 0.4,
      };

      setRipples(prev => [...prev, newRipple]);

      setTimeout(() => {
        setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
      }, 1500);
    }
  }, [isMobile]);

  const allRipples = [...ripples, ...autoRipples];

  if (isMobile) return null;

  return (
    <div 
      className="memory-ripples-container" 
      onClick={createRipple}
      onMouseMove={handleMouseMove}
    >
      {allRipples.map(ripple => (
        <div
          key={ripple.id}
          className={`memory-ripple memory-ripple--${ripple.type}`}
          style={{
            left: ripple.x,
            top: ripple.y,
            '--ripple-color': ripple.color,
            '--ripple-size': `${ripple.size}px`,
            '--ripple-intensity': ripple.intensity,
            '--ripple-speed': ripple.speed,
            '--ripple-decay': ripple.decay,
            '--ripple-rotation': `${ripple.rotation}rad`,
            '--ripple-rotation-speed': ripple.rotationSpeed,
            '--ripple-distortion': ripple.distortion,
          }}
        />
      ))}
    </div>
  );
};

export default MemoryRipples;
